/**
 * v1.4.1 — ContactsView: pantalla real de contactos (persistida vía /contacts).
 *
 * Reemplaza el vacío que dejaba el CRMView local-only (state efímero, sin
 * endpoint). Lista paginada + búsqueda + alta/edición/borrado (soft delete).
 * Estilo y helpers consistentes con boutique/BoutiqueViews.tsx.
 */
import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useContacts } from '../contacts/useContacts';
import { BulkImportModal } from './BulkImport';

interface Contact {
  id: string;
  firstName: string;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  company?: string | null;
  jobTitle?: string | null;
  source?: string | null;
  notes?: string | null;
}
interface Paginated<T> { data: T[]; total: number; page: number; pageSize: number; }

function L({ label, children }: { label: string; children: any }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500">{label}</span>
      {children}
    </label>
  );
}

// #249 — panel limitado a `max-h-[90vh]` con el cuerpo scrolleable y el header
// fijo, para que en tablets el formulario no desborde la pantalla dejando los
// botones fuera de alcance.
function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: any }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-6 py-4">
          <h3 className="font-serif text-lg tracking-tight text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">{children}</div>
      </div>
    </div>
  );
}

function Header({ title, subtitle, action }: { title: string; subtitle: string; action?: any }) {
  return (
    <div className="mb-5 flex items-end justify-between">
      <div>
        <h2 className="font-serif text-2xl tracking-tight text-slate-900">{title}</h2>
        <p className="text-sm text-slate-500">{subtitle}</p>
      </div>
      {action}
    </div>
  );
}

const btnPrimary = 'rounded-xl bg-[var(--brand-primary)] px-3 py-1.5 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--brand-primary)]';
const btnGhost = 'rounded-xl bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white';
const btnDanger = 'rounded-xl bg-white px-3 py-1.5 text-xs font-medium text-rose-700 ring-1 ring-rose-200 hover:bg-rose-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white';

const PAGE_SIZES = [10, 20, 50];

export function ContactsView({ canImport = false, onOpenProfile, onSendComunicado }: {
  canImport?: boolean;
  onOpenProfile?: (id: string) => void;
  /** V92 — entrada al composer de Comunicados (solo con communications:manage). */
  onSendComunicado?: () => void;
} = {}) {
 
  const [rows, setRows] = useState<Contact[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Fuente única: refrescar la cache compartida tras alta/edición/borrado para
  // que los selectores (CRM, leads, Boutique) reflejen el cambio sin recargar.
  const { refresh: refreshShared } = useContacts();

  const load = async (p = page, ps = pageSize, s = search) => {
    setLoading(true);
    try {
      const qs = `page=${p}&pageSize=${ps}${s.trim() ? `&search=${encodeURIComponent(s.trim())}` : ''}`;
      const r = (await api(`/contacts?${qs}`)) as Paginated<Contact>;
      setRows(r.data ?? []);
      setTotal(r.total ?? 0);
    } finally { setLoading(false); }
  };

  // Carga inicial + cambios de página/tamaño.
  useEffect(() => { load(page, pageSize, search); }, [page, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // Búsqueda debounced (resetea a página 1).
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setPage(1); load(1, pageSize, search); }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => { load(page, pageSize, search); void refreshShared(); };

  const remove = async (c: Contact) => {
    if (!window.confirm(`¿Archivar a ${c.firstName} ${c.lastName ?? ''}? No aparecerá más en la lista.`)) return;
    await api(`/contacts/${c.id}`, { method: 'DELETE' });
    refresh();
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div>
      <Header title="Contactos" subtitle="Tus clientes y prospectos."
        action={
          <div className="flex gap-2">
            {onSendComunicado && <button className={btnGhost} onClick={onSendComunicado}>Enviar comunicado</button>}
            {canImport && <button className={btnGhost} onClick={() => setImportOpen(true)}>Importar CSV</button>}
            <button className={btnPrimary} onClick={() => setCreateOpen(true)}>+ Nuevo contacto</button>
          </div>
        } />

      <div className="mb-3">
        <input className="input max-w-sm" placeholder="Buscar por nombre, email o teléfono…"
          value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {loading ? <p className="text-sm text-slate-500">Cargando…</p> : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2">Nombre</th>
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">Teléfono</th>
                <th className="px-4 py-2">Empresa</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">
                  {search.trim() ? 'Sin resultados para tu búsqueda.' : 'Aún no tienes contactos. Crea el primero para empezar.'}
                </td></tr>
              )}
              {rows.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50/60">
                  <td className="px-4 py-2">
                    <button className="font-medium text-slate-800 hover:text-slate-900 hover:underline"
                      onClick={() => (onOpenProfile ? onOpenProfile(c.id) : setEditing(c))}
                      title="Ver perfil del cliente">
                      {`${c.firstName} ${c.lastName ?? ''}`.trim()}
                    </button>
                  </td>
                  <td className="px-4 py-2 text-slate-600">{c.email ?? '—'}</td>
                  <td className="px-4 py-2 text-slate-600">{c.phone ?? '—'}</td>
                  <td className="px-4 py-2 text-slate-600">{c.company ?? '—'}</td>
                  <td className="px-4 py-2 text-right space-x-2">
                    {onOpenProfile && <button className={btnGhost} onClick={() => onOpenProfile(c.id)}>Perfil</button>}
                    <button className={btnGhost} onClick={() => setEditing(c)}>Editar</button>
                    <button className={btnDanger} onClick={() => remove(c)}>Borrar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Paginación */}
      <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
        <div className="flex items-center gap-2">
          <span>Por página:</span>
          <select className="input !w-auto !py-1" value={pageSize}
            onChange={(e) => { setPage(1); setPageSize(Number(e.target.value)); }}>
            {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <span>{total} contacto{total === 1 ? '' : 's'}</span>
        </div>
        <div className="flex items-center gap-2">
          <button className={btnGhost} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>← Anterior</button>
          <span>Página {page} de {totalPages}</span>
          <button className={btnGhost} disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Siguiente →</button>
        </div>
      </div>

      {createOpen && (
        <ContactModal onClose={() => setCreateOpen(false)} onSaved={() => { setCreateOpen(false); refresh(); }} />
      )}
      {importOpen && (
        <BulkImportModal onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); refresh(); }} />
      )}
      {editing && (
        <ContactModal initial={editing} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
          onDeleted={() => { setEditing(null); refresh(); }} />
      )}
    </div>
  );
}

function ContactModal({
  initial, onClose, onSaved, onDeleted,
}: {
  initial?: Contact;
  onClose: () => void;
  onSaved: () => void;
  onDeleted?: () => void;
}) {
  const isEdit = !!initial;
  const [f, setF] = useState({
    firstName: initial?.firstName ?? '',
    lastName: initial?.lastName ?? '',
    email: initial?.email ?? '',
    phone: initial?.phone ?? '',
    company: initial?.company ?? '',
    jobTitle: initial?.jobTitle ?? '',
    notes: initial?.notes ?? '',
  });
  const [err, setErr] = useState<string | null>(null);
  const [emailErr, setEmailErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const emailValid = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const submit = async (e: any) => {
    e.preventDefault(); setErr(null); setEmailErr(null);
    if (!f.firstName.trim()) { setErr('El nombre es obligatorio.'); return; }
    if (f.email.trim() && !emailValid(f.email.trim())) { setEmailErr('Email con formato inválido.'); return; }
    setBusy(true);
    const body = {
      firstName: f.firstName.trim(),
      lastName: f.lastName.trim() || undefined,
      email: f.email.trim() || undefined,
      phone: f.phone.trim() || undefined,
      company: f.company.trim() || undefined,
      jobTitle: f.jobTitle.trim() || undefined,
      notes: f.notes.trim() || undefined,
    };
    try {
      if (isEdit) await api(`/contacts/${initial!.id}`, { method: 'PUT', body });
      else await api('/contacts', { method: 'POST', body });
      onSaved();
    } catch (e2: any) {
      if (e2 instanceof ApiError && e2.status === 409) setEmailErr('Ya existe un contacto con ese email.');
      else setErr(e2?.body?.message || e2?.message || 'Error');
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!initial) return;
    if (!window.confirm(`¿Archivar a ${initial.firstName} ${initial.lastName ?? ''}?`)) return;
    setBusy(true);
    try { await api(`/contacts/${initial.id}`, { method: 'DELETE' }); onDeleted?.(); }
    catch (e2: any) { setErr(e2?.body?.message || e2?.message || 'Error'); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={isEdit ? 'Editar contacto' : 'Nuevo contacto'} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <L label="Nombre *"><input className="input" required value={f.firstName} onChange={(e) => setF({ ...f, firstName: e.target.value })} /></L>
          <L label="Apellido"><input className="input" value={f.lastName} onChange={(e) => setF({ ...f, lastName: e.target.value })} /></L>
        </div>
        <L label="Email">
          <input className="input" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
          {emailErr && <span className="mt-1 block text-[11px] text-rose-600">{emailErr}</span>}
          {/* V92 — leyenda de consentimiento (LFPDPPP): informa al capturar que el
              correo podrá recibir comunicados, con baja disponible en cada envío. */}
          <span className="mt-1 block text-[11px] text-slate-400">
            Al registrarlo, este contacto podrá recibir comunicados del negocio
            (con opción de baja en cada correo). Avísale al capturar su email.
          </span>
        </L>
        <div className="grid grid-cols-2 gap-3">
          <L label="Teléfono"><input className="input" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></L>
          <L label="Empresa"><input className="input" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} /></L>
        </div>
        <L label="Cargo"><input className="input" value={f.jobTitle} onChange={(e) => setF({ ...f, jobTitle: e.target.value })} /></L>
        <L label="Notas"><textarea className="input" rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></L>
        {err && <p className="alert-over rounded-xl px-3 py-2 text-xs">{err}</p>}
        <div className="flex items-center justify-between gap-2">
          <div>{isEdit && <button type="button" className={btnDanger} onClick={del} disabled={busy}>Borrar</button>}</div>
          <div className="flex gap-2">
            <button type="button" className={btnGhost} onClick={onClose}>Cancelar</button>
            <button className={btnPrimary} disabled={busy}>{busy ? 'Guardando…' : (isEdit ? 'Guardar' : 'Crear')}</button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
